import React, { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Chip,
  InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  Launch as LaunchIcon,
  Keyboard as KeyboardIcon,
} from "@mui/icons-material";
import { useSearchContext } from "contexts/SearchContext";

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: '600px',
      width: '90vw',
      maxHeight: '70vh',
      margin: 'auto',
      borderRadius: theme.spacing(2),
      overflow: 'hidden',
    },
  },
  dialogContent: {
    padding: '0 !important',
  },
  searchContainer: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(1),
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: `2px solid ${theme.palette.primary.main}`,
      },
    },
    '& .MuiInputBase-input': {
      fontSize: '16px',
      padding: theme.spacing(1.5),
    },
  },
  resultsList: {
    maxHeight: '400px',
    overflow: 'auto',
    padding: 0,
  },
  resultItem: {
    padding: theme.spacing(1.5, 2),
    cursor: 'pointer',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.selected': {
      backgroundColor: theme.palette.action.selected,
      '& .MuiListItemText-primary': {
        color: theme.palette.text.primary,
      },
      '& .MuiListItemText-secondary': {
        color: theme.palette.text.secondary,
      },
      '& .MuiChip-root': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      },
    },
  },
  resultTitle: {
    fontWeight: 600,
    fontSize: '14px',
    marginBottom: theme.spacing(0.5),
  },
  resultCategory: {
    fontSize: '12px',
    opacity: 0.8,
  },
  noResults: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  shortcutHint: {
    padding: theme.spacing(1, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '12px',
    color: theme.palette.text.secondary,
  },
  shortcutChip: {
    height: '20px',
    fontSize: '10px',
    backgroundColor: theme.palette.grey[200],
  },
  categoryChip: {
    height: '18px',
    fontSize: '10px',
    marginLeft: theme.spacing(1),
  },
}));

const GlobalPageSearch = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  const {
    isOpen,
    searchTerm,
    filteredPages,
    selectedIndex,
    closeSearch,
    handleSearchChange,
    setSelectedIndex,
  } = useSearchContext();

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Use requestAnimationFrame for better timing
      const focusInput = () => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      };

      // Try multiple approaches for better reliability
      requestAnimationFrame(focusInput);
      setTimeout(focusInput, 50);
      setTimeout(focusInput, 150);
    }
  }, [isOpen]);

  // Handle page navigation
  const handlePageSelect = useCallback((page) => {
    navigate(page.url);
    closeSearch();
  }, [navigate, closeSearch]);

  // Handle keyboard navigation within search modal
  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          closeSearch();
          break;
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex(
            selectedIndex < filteredPages.length - 1 ? selectedIndex + 1 : selectedIndex
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex(selectedIndex > 0 ? selectedIndex - 1 : selectedIndex);
          break;
        case 'Enter':
          event.preventDefault();
          if (filteredPages[selectedIndex]) {
            handlePageSelect(filteredPages[selectedIndex]);
          }
          break;
        default:
          // No action needed for other keys
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredPages, selectedIndex, closeSearch, setSelectedIndex, handlePageSelect]);

  // Scroll selected item into view
  useEffect(() => {
    const selectedElement = document.querySelector('.selected');
    if (selectedElement) {
      selectedElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedIndex]);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={closeSearch}
        className={classes.dialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent className={classes.dialogContent}>
          <Box className={classes.searchContainer}>
            <TextField
              ref={searchInputRef}
              fullWidth
              autoFocus
              placeholder="Search pages..."
              value={searchTerm}
              onChange={handleSearchChange}
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {filteredPages.length > 0 ? (
            <List className={classes.resultsList}>
              {filteredPages.map((page, index) => (
                <ListItem
                  key={page.id}
                  className={`${classes.resultItem} ${
                    index === selectedIndex ? 'selected' : ''
                  }`}
                  onClick={() => handlePageSelect(page)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center">
                        <Typography className={classes.resultTitle}>
                          {page.title}
                        </Typography>
                        <Chip
                          label={page.category}
                          size="small"
                          className={classes.categoryChip}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Typography className={classes.resultCategory}>
                        {page.url}
                      </Typography>
                    }
                  />
                  <LaunchIcon fontSize="small" style={{ opacity: 0.5 }} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box className={classes.noResults}>
              <Typography variant="body2">
                {searchTerm ? 'No pages found' : 'Type to search pages...'}
              </Typography>
            </Box>
          )}

          <Box className={classes.shortcutHint}>
            <Box display="flex" alignItems="center" gap={1}>
              <KeyboardIcon fontSize="small" />
              <span>Navigation:</span>
              <Chip label="↑↓" size="small" className={classes.shortcutChip} />
              <span>to navigate</span>
              <Chip label="Enter" size="small" className={classes.shortcutChip} />
              <span>to select</span>
            </Box>
            <Chip label="Esc" size="small" className={classes.shortcutChip} />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GlobalPageSearch;